import { sendGTMEvent } from "@/lib/utils/tracking/sendGTMEvent"
import { useEffect, useRef } from "react"

/**
 * Payload for the `eyefitu_init` dataLayer event consumed by EyeFitU.
 *
 * `recommender` is the consent-driven toggle: when `true` EyeFitU loads the full
 * experience (recommender + size guides); when `false` only the limited experience
 * (size guides). It is derived from the visitor's functional/preferences consent.
 *
 * Contract: the EyeFitU GTM tag reads this `recommender` flag from the `eyefitu_init`
 * event in its `isOnlySizeChart` gate — `false` forces the size-chart-only path and hides
 * the "Find my size" recommender. Keep this property name in sync with that GTM tag.
 */
export interface EyeFitUInitData {
    canonicalCategory: string
    gender: string
    sizeLabels: string[]
    mainImage: string
    productName: string
    recommender: boolean
    sizeGuideType?: string
    sizeGuideName?: string
}

// If the consent state has not resolved within this window (e.g. Cookiebot is slow,
// blocked, or absent) we initialise EyeFitU anyway with the current (limited) consent so
// the size guides always load. A later consent resolution re-fires with the correct flag.
const CONSENT_FALLBACK_MS = 4000

/**
 * Pushes `eyefitu_init` to the dataLayer in coordination with Cookiebot consent.
 *
 * - Waits until the consent decision is resolved (`consentReady` — Cookiebot
 *   `OnConsentReady`) before the first push. This guarantees `eyefitu_init` lands in the
 *   dataLayer *after* the cookie consent events, which EyeFitU requires: their GTM tag
 *   reads the resolved consent from the cookie events when `eyefitu_init` fires. Firing on
 *   the earlier `OnLoad` signal pushed `eyefitu_init` before a fresh visitor had made a
 *   cookie choice, so it landed before the cookie events.
 * - Re-fires whenever the payload changes — i.e. on product change (color navigation) or
 *   when the visitor updates their consent and the `recommender` flag flips — so EyeFitU
 *   can re-evaluate and swap between the full and limited experience.
 */
export const useEyeFitUIEvent = (data: EyeFitUInitData | null, consentReady: boolean) => {
    const lastSignatureRef = useRef<string | null>(null)

    useEffect(() => {
        if (!data) return

        const push = (trigger: "consent-ready" | "consent-timeout-fallback") => {
            const signature = JSON.stringify(data)
            if (lastSignatureRef.current === signature) return

            const event = { event: "eyefitu_init", ...data }

            sendGTMEvent(event)
            lastSignatureRef.current = signature

            // Demonstration log (QA/dev only) so EyeFitU can see exactly what we push and
            // when. `recommender` is the consent-driven full/limited toggle.
            if (process.env.NEXT_PUBLIC_ENVIRONMENT !== "production") {
                console.info("[EyeFitU] pushed eyefitu_init →", {
                    trigger,
                    recommender: data.recommender,
                    experience: data.recommender
                        ? "full (recommender + size guides)"
                        : "limited (size guides only)",
                    event
                })
            }
        }

        // Consent decision is known (Cookiebot `OnConsentReady`): push now. This effect
        // already runs asynchronously after Cookiebot's own event handlers, so the cookie
        // consent events it pushes on the same tick land in the dataLayer before this.
        if (consentReady) {
            push("consent-ready")
            return
        }

        // Consent unresolved (Cookiebot slow/blocked/absent): initialise anyway after a
        // short wait so the limited experience (size guides) always loads. If consent
        // resolves first, this timer is cleared and the push runs with the correct flag.
        const timer = window.setTimeout(
            () => push("consent-timeout-fallback"),
            CONSENT_FALLBACK_MS
        )
        return () => window.clearTimeout(timer)
    }, [data, consentReady])
}
